/**
 * GameLaunchCoordinator
 *
 * Manages Playjector (game launcher) lifecycle and state.
 *
 * Responsibilities:
 * - Track game launch/close lifecycle
 * - Monitor Steam login state
 * - Handle game launch timeouts and error scenarios
 * - Manage campaign launch flow (game launching only)
 * - Send game lifecycle analytics
 *
 * ARCHITECTURE:
 * - Owns game/playjector state (gameLaunched, gameId, gameName, etc.)
 * - Does NOT own matchmaking state (campaignId, matchmakeable, etc.)
 * - Communicates with PavilionCoordinator via EVENTS ONLY
 * - PavilionCoordinator handles all API communication and matchmaking state
 *
 * Event Flow:
 * - PavilionCoordinator.campaignAssigned → GameLaunchCoordinator launches game
 * - Game launches → GameLaunchCoordinator.campaignLaunchSucceeded → PavilionCoordinator updates state + API
 * - Game fails → GameLaunchCoordinator.campaignLaunchFailed → PavilionCoordinator updates state + API
 *
 * Status: Campaign launch flow implemented with timeout tracking
 */

import { makeStateStore } from '@playcastdotio/StateStore';
import type { StateStore, PlayjectorHostEdition } from '@playcastdotio/CoreTypes';
import { GameLifecycleManager } from './managers/GameLifecycleManager';
import { CampaignSwitchManager } from './managers/CampaignSwitchManager';
import { MacroManager } from './managers/MacroManager';
import type { IPlayjectorService, PlayjectorState, CampaignLaunchResult, InstanceCapabilities } from '../../services/playjector/interfaces/IPlayjectorService';
import type { AppCoordinator } from '../app/AppCoordinator';
import { PavilionCoordinator } from '../pavilion/PavilionCoordinator';
import type { SessionCoordinator } from '../session/SessionCoordinator';
import type { IReplicationRouter } from '../ReplicationRouter';
import { CoordinatorEventImpl } from '../_types/events';
import type { IHostAnalyticsManager } from '../../services/analytics/IHostAnalyticsManager';
import type { GameSaveCampaign, IGameSaveManager } from '../../managers/game-save/IGameSaveManager';
import type {
  GameSaveConfigurationAvailabilityResult,
  IGameSaveConfigurationAvailability,
} from '../../services/gameSave/IGameSaveConfigurationAvailability';
import type { ICampaignGameSaveConfigurationStore } from '../../managers/game-save/GameSaveManager';
import * as Sentry from '@sentry/react';
import {
  generateMatchmakingV2Message,
  MatchmakingV2MessageType,
  type GamingScorePersistedData,
  type HostCampaignConfig,
  type MatchmakingV2ErrorPayload,
  type ProcessLaunchPackage,
} from '@playcastdotio/Messaging';
import { GameInstanceStore, type GameInstanceSessionRecord } from '../game-instance/GameInstanceStore';
import { GameInstanceAllocator } from '../game-instance/GameInstanceAllocator';
import { effectiveMaxPlayers, hardwareCapFromGamingScore } from '../game-instance/effectiveMaxPlayers';
import { GameInstanceLaunchManager } from '../game-instance/managers/GameInstanceLaunchManager';
import { GameInstanceReportManager, type HostStateReportPayload } from '../game-instance/managers/GameInstanceReportManager';
import { MatchLifecycleManager } from '../game-instance/managers/MatchLifecycleManager';
import { AndroidCampaignLauncher } from './android/AndroidCampaignLauncher';
import { createInstanceLaunchedHandler } from '../../api/messaging/handlers/instanceLaunched';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import { machineIdState } from '../../api/stores/shared';
import { hostCapacityConfigState } from '../game-instance/hostCapacityConfig';
import { alignHostCapacityForCampaign } from '../game-instance/alignHostCapacityForCampaign';


// ========================================
// Types
// ========================================

/**
 * Consecutive MATCHMAKING_V2_DISABLED rejections tolerated before v2 reporting
 * backs off (pool-match-lifecycle Phase 6). Two covers the initial report plus
 * one heartbeat without treating a single racing rejection as terminal.
 */
const V2_DISABLED_ERROR_BACKOFF_THRESHOLD = 2;

export type GameLaunchCoordinatorState = {
  initialized: boolean;
};

// Shared state types for managers
export type GameLaunchModeState = {
  gameLaunched: boolean;
  gameName: string;
  gameId: number;
  steamLaunched: boolean;
  steamWindow: boolean;
  userLoggedIn: boolean;
};

export type GameLaunchMatchmakerState = {
  stateDetails?: {
    currentMatchInvitationId?: string;
  };
};

// NOTE: Matchmaking state is owned by PavilionCoordinator, not this coordinator

export type AdModeState = {
  enabled: boolean;
};

const GAME_SAVE_PROCESS_STOP_TIMEOUT_MS = 30_000;
type CampaignSavePreparation =
  | { status: 'ready'; configured: boolean }
  | { status: 'failed'; reason: string }
  | { status: 'superseded' };

// Event types imported from shared location

// ========================================
// GameLaunchCoordinator (Skeleton)
// ========================================

export class GameLaunchCoordinator {
  private state: StateStore<GameLaunchCoordinatorState>;

  // Per-launch idempotency flag for `signalLaunchMacroWalkComplete()`.
  // Reset on every fresh `gameLaunched` event so each launch can emit `gameReady`
  // exactly once. Defaults to `true` until the first launch starts so we never
  // emit `gameReady` for a never-launched coordinator.
  private launchMacroWalkComplete = true;

  // Snapshot of the most recent gameLaunched event payload. Used by both
  // `signalLaunchMacroWalkComplete()` (stateful walk completion) and
  // `executeLegacyOnLaunchMacro()` (legacy fallback) so the gameReady event
  // carries identical (gameId, gameName) regardless of which path completed.
  private lastLaunchData: { gameId: number; gameName: string } | null = null;
  private launchMacroGeneration = 0;
  private gameSaveRestoreGeneration: number | null = null;
  /** A checked save must never silently become a legacy launch in this host session. */
  private readonly confirmedConfiguredSaveKeys = new Set<string>();

  // Stores (GameLaunchCoordinator owns game/playjector state ONLY)
  private gameModeStore: StateStore<GameLaunchModeState>;
  private matchmakerStore: StateStore<GameLaunchMatchmakerState>;
  private adModeStore: StateStore<AdModeState>;

  // NOTE: Matchmaking state (campaignId, matchmakeable, etc.) is owned by PavilionCoordinator!
  // We communicate via events, not by modifying each other's state.

  private managers: {
    lifecycle: GameLifecycleManager;
    campaignSwitch: CampaignSwitchManager;
    macro: MacroManager;
  };

  // Service and external coordinator references
  private playjectorService: IPlayjectorService;
  private pavilionCoordinator?: PavilionCoordinator;
  private unsubscribeFromService?: () => void;
  private readonly gameInstanceStore: GameInstanceStore;
  private readonly gameInstanceAllocator: GameInstanceAllocator;
  private readonly gameInstanceLaunchManager: GameInstanceLaunchManager;
  private readonly gameInstanceReportManager: GameInstanceReportManager;
  private readonly matchLifecycleManager: MatchLifecycleManager;
  private readonly androidLauncher: AndroidCampaignLauncher;
  private unsubscribeInstanceLaunched?: () => void;

  /** Machine capabilities resolver (Phase 3); default "unknown" = clamp no-op. */
  private instanceCapabilitiesResolver: () => InstanceCapabilities | undefined = () => undefined;

  /**
   * Authoritative gaming-score provider (pool-match-lifecycle Phase 5).
   * Late-bound via `setHardwareInfoProvider` once GamingScoreCoordinator
   * exists; defaults to "unknown" so the configured maxPlayers stands alone.
   */
  private hardwareInfoProvider: () => GamingScorePersistedData | null = () => null;
  private capacityConfigListenerId: string | null = null;

  /**
   * Consecutive MATCHMAKING_V2_ERROR replies with code MATCHMAKING_V2_DISABLED
   * from the API (pool-match-lifecycle Phase 6). Reset whenever reporting
   * (re)starts for a v2 campaign.
   */
  private consecutiveV2DisabledErrors = 0;
  private campaignLaunchGeneration = 0;

  public events = {
    gameLaunched: new CoordinatorEventImpl<{ gameId: number; gameName: string }>(),
    gameClosed: new CoordinatorEventImpl<{ gameId: number; gameName: string }>(),
    steamUserLoggedOut: new CoordinatorEventImpl<void>(),
    logoutTimeoutExpired: new CoordinatorEventImpl<void>(),
    gameInfoUpdated: new CoordinatorEventImpl<{ gameId: number; gameName: string }>(),
    // Campaign switch events (for cross-coordinator communication with PavilionCoordinator)
    campaignSwitchSucceeded: new CoordinatorEventImpl<{ campaignId: string }>(),
    campaignSwitchFailed: new CoordinatorEventImpl<{ campaignId: string; reason: string; transient?: boolean }>(),
    gameSaveRestoreStarted: new CoordinatorEventImpl<{ campaignId: string }>(),
    gameSaveCampaignConfigurationSelected: new CoordinatorEventImpl<{
      campaignId: string; generation: number; configuration: HostCampaignConfig['gameSave'];
    }>(),
    gameSaveRestored: new CoordinatorEventImpl<{ campaignId: string }>(),
    gameSaveConfigurationChecked: new CoordinatorEventImpl<{ campaignId: string; configured: boolean }>(),
    gameSaveLaunchDispatched: new CoordinatorEventImpl<{ campaignId: string; generation: number }>(),
    gameSaveLaunchConfirmed: new CoordinatorEventImpl<{ campaignId: string; generation: number }>(),
    // Fires after gameLaunched AND after onLaunchMacro completes (used by PavilionCoordinator to set matchmakeable)
    gameReady: new CoordinatorEventImpl<{ gameId: number; gameName: string }>(),
    instanceShadowcamsChanged: new CoordinatorEventImpl<{ shadowCamNames: readonly string[] }>(),
    instanceSessionsChanged: new CoordinatorEventImpl<{ sessions: GameInstanceSessionRecord[] }>(),
  };

  constructor(
    playjectorService: IPlayjectorService,
    private readonly appCoordinator: AppCoordinator,
    private readonly analytics: IHostAnalyticsManager,
    private readonly realtimeService?: IRealtimeService,
    private readonly gameSaveManager?: IGameSaveManager,
    private readonly gameSaveConfigurationAvailability?: IGameSaveConfigurationAvailability,
    private readonly campaignGameSaveConfiguration?: ICampaignGameSaveConfigurationStore,
  ) {
    console.log('[GameLaunchCoordinator] Constructing');

    this.playjectorService = playjectorService;
    this.gameInstanceStore = new GameInstanceStore();
    // Pool-match-lifecycle Phase 5: allocator capacity is the host's effective
    // max (min(ops maxPlayers, hardware cap)), replacing the hardcoded 9.
    this.gameInstanceAllocator = new GameInstanceAllocator(this.gameInstanceStore, this.getHostEffectiveMax());
    this.gameInstanceLaunchManager = new GameInstanceLaunchManager(
      playjectorService,
      this.gameInstanceStore,
      this.gameInstanceAllocator,
      (shadowCamNames) => this.events.instanceShadowcamsChanged.emit({ shadowCamNames }),
      (sessions) => {
        this.events.instanceSessionsChanged.emit({ sessions });
      },
    );
    this.gameInstanceReportManager = new GameInstanceReportManager(
      this.gameInstanceStore,
      () => this.buildMachineInfo(),
    );
    // Pool match lifecycle (pool-match-lifecycle Phase 2): consumes MATCH_ASSIGNED /
    // CAMPAIGN_SWITCH, answers HOST_MATCH_READY, emits MATCH_ENDED, and gives
    // teardownInstance its first production caller on failure/eviction.
    this.matchLifecycleManager = new MatchLifecycleManager(
      this.gameInstanceStore,
      this.realtimeService,
      this.gameInstanceLaunchManager,
      () => this.appCoordinator.getHostAuthId(),
    );
    this.matchLifecycleManager.attach();
    // Pool-match-lifecycle Phase 6: route API v2 rejections to the report
    // backoff so a flag-off API is not spammed.
    this.matchLifecycleManager.setV2ErrorHandler((payload) => this.noteMatchmakingV2Error(payload));
    // Phase 8 (review fix S1): stale-stream guard — instanceClosed events from
    // a previous campaign's stream are ignored while a new campaign runs.
    this.matchLifecycleManager.setActiveAndroidStreamResolver(
      () => this.gameInstanceLaunchManager.getActiveAndroidStreamId(),
    );

    // Android multi-instance Phase 3: single-launch orchestrator, plus
    // per-instance reply routing into the launch manager (separate
    // registration from the legacy pavilionLaunchReply correlation).
    this.androidLauncher = new AndroidCampaignLauncher(this.gameInstanceLaunchManager, {
      cleanup: (authId) => this.cleanupGameInstances(authId),
      v1GameState: (launched, gameName) => this.driveAndroidV1GameState(launched, gameName),
      switchSucceeded: (id) => this.reportCampaignSwitchSuccess(id),
      switchFailed: (id, reason) => this.reportCampaignSwitchFailure(id, reason, false),
      startV2Reporting: () => this.startInstanceReporting(),
    });
    this.unsubscribeInstanceLaunched = playjectorService.onInstanceLaunched(
      createInstanceLaunchedHandler(() => this.gameInstanceLaunchManager),
    );

    // Re-evaluate effective capacity when the ops maxPlayers setting changes.
    this.capacityConfigListenerId = hostCapacityConfigState.addListener(() => this.applyHostCapacity());

    this.state = makeStateStore<GameLaunchCoordinatorState>({
      initialized: false,
    });

    // Initialize stores
    this.gameModeStore = makeStateStore<GameLaunchModeState>({
      gameLaunched: false,
      gameName: '',
      gameId: 0,
      steamLaunched: false,
      steamWindow: false,
      userLoggedIn: false,
    });

    this.matchmakerStore = makeStateStore<GameLaunchMatchmakerState>({
      stateDetails: {},
    });

    // Matchmaking state is owned by PavilionCoordinator, not us!

    this.adModeStore = makeStateStore<AdModeState>({
      enabled: false,
    });

    // NOTE: MacroManager needs PavilionCoordinator reference, but we don't have it yet
    // MacroManager will be initialized in init() method after pavilionCoordinator is passed
    this.managers = {
      lifecycle: new GameLifecycleManager(
        {
          gameMode: this.gameModeStore,
          matchmaker: this.matchmakerStore,
          adMode: this.adModeStore,
        },
        this.events,
        this.appCoordinator,
        this.analytics,
      ),
      campaignSwitch: new CampaignSwitchManager(
        {
          gameMode: this.gameModeStore,
          matchmaker: this.matchmakerStore,
          adMode: this.adModeStore,
        },
      ),
      // MacroManager initialized in init()
      macro: null as unknown as MacroManager, // Temporary placeholder
    };
  }

  /**
   * Initialize the coordinator
   */
  public async init(pavilionCoordinator?: PavilionCoordinator, sessionCoordinator?: SessionCoordinator): Promise<void> {
    console.log('[GameLaunchCoordinator] Initializing');

    // Store pavilionCoordinator reference (needed for MacroManager)
    this.pavilionCoordinator = pavilionCoordinator;

    // Initialize MacroManager (requires pavilionCoordinator)
    if (pavilionCoordinator) {
      this.managers.macro = new MacroManager(pavilionCoordinator, this.analytics);
      console.log('[GameLaunchCoordinator] ✅ MacroManager initialized');
    } else {
      console.warn('[GameLaunchCoordinator] ⚠️  MacroManager skipped (no PavilionCoordinator)');
      Sentry.logger.warn('[GameLaunchCoordinator] ⚠️  MacroManager skipped (no PavilionCoordinator)')
    }

    // Subscribe to Playjector state changes
    this.unsubscribeFromService = this.playjectorService.onStateChange((playjectorState) => {
      this.handlePlayjectorStateChange(playjectorState);
    });

    // Subscribe to campaign launch results (pavilionLaunchReply)
    this.playjectorService.onCampaignLaunchResult((result) => {
      this.handleCampaignLaunchResult(result);
    });

    // Listen to PavilionCoordinator events (proper cross-coordinator communication!)
    if (pavilionCoordinator) {

      // When campaign config loaded → launch game with timeout tracking
      // NOTE: We listen to campaignConfigLoaded (not campaignAssigned) to ensure
      // the campaign config is fetched before we try to launch
      pavilionCoordinator.events.campaignConfigLoaded.addListener(() => {
        console.log('[GameLaunchCoordinator] Campaign config loaded, launching game');

        Sentry.logger.info('[GameLaunchCoordinator] Campaign config loaded, launching game'); // This is temp!
        
        this.launchCampaignWithTracking(pavilionCoordinator);
      });

      // NOTE: Game should be running BEFORE match found.
      // When match found, PavilionCoordinator orchestrates stream start via SessionCoordinator.
      // GameLaunchCoordinator does not need to do anything on match found.
    }

    // Initialize managers
    this.managers.lifecycle.init(sessionCoordinator);
    this.managers.campaignSwitch.init();

    // Send initial Pavilion edition to Playjector
    // This ensures Playjector knows we're in Pavilion mode from the start
    if (pavilionCoordinator) {
      console.log('[GameLaunchCoordinator] Sending initial Pavilion edition to Playjector');
      const isGuestAccount = window.playcast?.guestAccount ?? false;
      this.playjectorService.sendHostModeChanged(
        'pavilion',
        false, // matchmakeable (will be set when game launches)
        false, // steamGuestLogin (will be set when credentials available)
        isGuestAccount, // Read actual guest account status from window.playcast
        '',    // credentials (empty for now)
        pavilionCoordinator.getAuthId() || '',
        undefined // campaignId (not assigned yet)
      );
    }

    // Listen to gameLaunched event. Whether we run the legacy on-launch macro
    // or hand off to StatefulMacroCoordinator depends on the campaign config.
    // The fork lives here so the per-launch flow remains a single observable
    // chain (`gameLaunched -> [legacy | stateful walk] -> gameReady`).
    this.events.gameLaunched.addListener(async (data) => {
      if (this.gameSaveRestoreGeneration === this.campaignLaunchGeneration) return;
      // Each fresh launch resets the per-launch idempotency flag for the
      // gameReady emit. The two completion paths (legacy + stateful) both
      // funnel through `signalLaunchMacroWalkComplete()` so this is the
      // single source of truth for "we have not yet emitted gameReady for
      // *this* launch".
      this.lastLaunchData = data;
      this.launchMacroWalkComplete = false;
      this.launchMacroGeneration += 1;

      if (this.shouldUseStatefulMacroSystem()) {
        console.log(
          '[GameLaunchCoordinator] Game launched, useStatefulMacroSystem=true; ' +
          'StatefulMacroCoordinator will drive the walk and call signalLaunchMacroWalkComplete()',
        );
        return;
      }

      console.log('[GameLaunchCoordinator] Game launched, running legacy onLaunchMacro path');
      await this.executeLegacyOnLaunchMacro();
    });

    // Listen to gameClosed event to reset macro tracking
    this.events.gameClosed.addListener(() => {
      if (this.managers.macro) {
        console.log('[GameLaunchCoordinator] Game closed, resetting macro tracking');
        this.managers.macro.reset();
      }
    });

    this.state.updateState({ initialized: true });
    console.log('[GameLaunchCoordinator] Initialized');
  }

  /**
   * Register IPC replication handler with the replication router
   *
   * Called during coordinator initialization to set up message routing.
   *
   * Pattern (follows MessageRouter):
   * - Coordinator registers handler for specific IPC channel
   * - When IPC replication arrives, router calls our handler
   * - Handler updates store, manager emits events
   */
  public registerReplicationHandler(router: IReplicationRouter): void {
    router.register('playjectorHostModeState', this.onReplicationReceived.bind(this));
    console.log('[GameLaunchCoordinator] Registered replication handler');
  }

  /**
   * Handle Playjector state changes from service
   * @private
   *
   * Simply updates the store - manager will detect changes and emit events
   */
  private handlePlayjectorStateChange(playjectorState: PlayjectorState): void {
    // Update store with new state
    // Manager listens to store changes and emits events
    this.gameModeStore.updateState({
      gameLaunched: playjectorState.gameLaunched,
      gameName: playjectorState.gameName,
      gameId: playjectorState.gameId,
      steamLaunched: playjectorState.steamLaunched,
      steamWindow: playjectorState.steamWindow,
      userLoggedIn: playjectorState.userLoggedIn,
    });
  }

  /**
   * Get initialization status
   */
  public isInitialized(): boolean {
    return this.state.currentState.initialized;
  }

  /**
   * Stop the running game by terminating the game process (not Playjector itself).
   * Used when the API clears the campaign and we need to terminate the game process.
   */
  public stopGame(hostAuthId: string): void {
    if (!this.gameModeStore.currentState.gameLaunched) {
      console.log('[GameLaunchCoordinator] No game running, nothing to stop');
      return;
    }

    console.log('[GameLaunchCoordinator] Stopping game (terminating pavilion process)');
    this.managers.campaignSwitch.cancelPendingSwitch('campaign_cleared');
    this.playjectorService.terminateGame(hostAuthId);
  }

  /**
   * Get current game state
   */
  public getGameState(): { gameLaunched: boolean; gameId: number; gameName: string } {
    const { gameLaunched, gameId, gameName } = this.gameModeStore.currentState;
    return { gameLaunched, gameId, gameName };
  }

  public getGameInstanceSessions(): GameInstanceSessionRecord[] {
    return this.gameInstanceLaunchManager.getSessions();
  }

  public getGameInstanceLaunchManager(): GameInstanceLaunchManager {
    return this.gameInstanceLaunchManager;
  }

  public getGameInstanceReportManager(): GameInstanceReportManager {
    return this.gameInstanceReportManager;
  }

  public getMatchLifecycleManager(): MatchLifecycleManager {
    return this.matchLifecycleManager;
  }

  /**
   * Effective per-host instance capacity (pool-match-lifecycle Phase 5,
   * extended in android-multi-instance-host Phase 3):
   * `min(hostConfig.maxPlayers, reportedMaxGameInstances)`. The gaming-score
   * hardware tier is no longer a clamp (advisory telemetry only) — ops
   * `maxPlayers` plus the machine-reported ceiling are authoritative. The
   * reported ceiling is `undefined` (no-op) until
   * `setInstanceCapabilitiesResolver` is wired.
   */
  public getHostEffectiveMax(): number {
    return effectiveMaxPlayers(
      { maxPlayers: hostCapacityConfigState.currentState.maxPlayers },
      this.hardwareInfoProvider(),
      this.instanceCapabilitiesResolver()?.maxGameInstances,
    );
  }

  /** Late-bind the InstanceCapabilitiesProvider (init.ts); re-evaluates capacity. */
  public setInstanceCapabilitiesResolver(resolver: () => InstanceCapabilities | undefined): void {
    this.instanceCapabilitiesResolver = resolver;
    this.applyHostCapacity();
  }

  /**
   * Late-bind the authoritative gaming-score provider (wired by init.ts once
   * GamingScoreCoordinator exists — same pattern as the report manager's
   * `setInstancePeerResolver`). Re-evaluates capacity immediately.
   */
  public setHardwareInfoProvider(provider: () => GamingScorePersistedData | null): void {
    this.hardwareInfoProvider = provider;
    this.applyHostCapacity();
  }

  /** Re-size the allocator and refresh the report after a capacity input changed. */
  private applyHostCapacity(): void {
    this.gameInstanceAllocator.setCapacity(this.getHostEffectiveMax());
    this.gameInstanceReportManager.flush();
  }

  /** Machine-level identity for host state reports; null until machine id is known. */
  private buildMachineInfo() {
    const machineId = machineIdState.currentState;
    if (!machineId) return null;
    return {
      machineId,
      geohash: this.appCoordinator.getGeohash(),
      coords: this.appCoordinator.getCoords(),
      isCloudHost: this.appCoordinator.getIsCloudHost(),
      guestAccount: this.appCoordinator.getGuestAccount(),
      maxPlayers: this.getHostEffectiveMax(),
      // Phase 8 (review fix S2): the applied CONFIG maxPlayers (pre-clamp) so
      // the API's desired-capacity marker clears even when the effective
      // maxPlayers above is clamped below it.
      configuredMaxPlayers: hostCapacityConfigState.currentState.maxPlayers,
    };
  }

  /** Begin MATCHMAKING_V2_HOST_STATE_REPORT emission (no-op without realtime). */
  private startInstanceReporting(): void {
    if (!this.realtimeService) return;
    // A fresh v2 campaign re-arms the API error backoff (Phase 6).
    this.consecutiveV2DisabledErrors = 0;
    this.gameInstanceReportManager.startReporting((payload: HostStateReportPayload) => {
      this.realtimeService?.sendMessage(
        generateMatchmakingV2Message(
          MatchmakingV2MessageType.MATCHMAKING_V2_HOST_STATE_REPORT,
          payload,
        ) as unknown as Parameters<IRealtimeService['sendMessage']>[0],
      );
    });
  }

  /**
   * Pool-match-lifecycle Phase 6: while the API's matchmaking-v2 flag is off it
   * answers every v2 message with MATCHMAKING_V2_ERROR(MATCHMAKING_V2_DISABLED).
   * After a small number of consecutive rejections, stop reporting and log once
   * — a flag-off API must not be spammed. Reporting (and the counter) restarts
   * with the next v2 campaign launch.
   */
  private noteMatchmakingV2Error(payload: MatchmakingV2ErrorPayload): void {
    if (payload.error?.code !== 'MATCHMAKING_V2_DISABLED') return;
    this.consecutiveV2DisabledErrors += 1;
    if (this.consecutiveV2DisabledErrors < V2_DISABLED_ERROR_BACKOFF_THRESHOLD) return;
    if (!this.gameInstanceReportManager.isReporting()) return;
    console.warn(
      '[GameLaunchCoordinator] API reports matchmaking v2 disabled — ' +
      'stopping v2 host state reports until the next v2 campaign launch',
    );
    Sentry.logger.warn(
      '[GameLaunchCoordinator] API reports matchmaking v2 disabled — stopping v2 host state reports',
    );
    this.gameInstanceReportManager.stopReporting();
  }

  public getGameInstanceStore(): GameInstanceStore {
    return this.gameInstanceStore;
  }

  public async cleanupGameInstances(hostAuthId: string): Promise<void> {
    // Android stream teardown keeps v1 machine state truthful (gameLaunched
    // false); legacy sessions never have an active android stream.
    const hadAndroidStream = this.gameInstanceLaunchManager.getActiveAndroidStreamId() !== undefined;
    this.gameInstanceReportManager.stopReporting();
    await this.gameInstanceLaunchManager.cleanup(hostAuthId);
    if (hadAndroidStream) this.driveAndroidV1GameState(false, '');
    this.events.instanceSessionsChanged.emit({ sessions: [] });
  }

  /** Dev-harness entry for the android path (Ctrl+F12 overlay). */
  public launchDevelopmentAndroidInstances(
    hostAuthId: string,
    campaign: HostCampaignConfig,
  ): Promise<void> {
    return this.runAndroidCampaign(campaign.campaignId, hostAuthId, campaign);
  }

  /**
   * Check if there's a pending campaign switch (for testing)
   */
  public hasPendingCampaignSwitch(): boolean {
    return this.managers.campaignSwitch.hasPendingSwitch();
  }

  /**
   * Get the campaign ID of the pending switch (for testing)
   */
  public getPendingCampaignId(): string | null {
    return this.managers.campaignSwitch.getPendingCampaignId();
  }

  /** Internal lifecycle events must belong to the current launch attempt. */
  public isCurrentCampaignLaunchGeneration(generation: number): boolean {
    return generation === this.campaignLaunchGeneration;
  }

  /**
   * Report campaign launch result directly (for testing)
   *
   * In production, this is called by Playjector via IPC messages.
   * For tests, this allows direct result reporting.
   */
  public reportCampaignLaunchResult(result: 'SUCCESS' | 'FAILURE', reason?: string): void {
    this.managers.campaignSwitch.reportResult(result, reason);
  }

  /**
   * Register a settle guard on the internal CampaignSwitchManager so long
   * postMacroDelayMs waits don't trip the campaign-switch timeout.
   */
  public setCampaignSwitchSettleGuard(guard: () => boolean): void {
    this.managers.campaignSwitch.setSettleGuard(guard);
  }

  /**
   * Execute onJoinMacro (called by PavilionCoordinator before starting stream)
   *
   * This triggers campaign-specific actions like unpausing the game.
   * Awaited before HOST_READY is sent and the player can connect.
   */
  public async executeOnJoinMacro(): Promise<void> {
    if (this.managers.macro) {
      await this.managers.macro.executeOnJoinMacro();
    } else {
      console.warn('[GameLaunchCoordinator] ⚠️  MacroManager not initialized, cannot execute onJoinMacro');
    }
  }

  /**
   * Execute onExitMacro (called by PavilionCoordinator when all players leave, no restart)
   *
   * Awaited before re-entering the matchmaking pool. After the macro completes,
   * the inactive loop is started automatically.
   */
  public async executeOnExitMacro(): Promise<void> {
    if (this.managers.macro) {
      await this.managers.macro.executeOnExitMacro();
    } else {
      console.warn('[GameLaunchCoordinator] ⚠️  MacroManager not initialized, cannot execute onExitMacro');
    }
  }

  /**
   * Signal that the launch macro walk has completed.
   *
   * Called by:
   *  - `executeLegacyOnLaunchMacro()` after the legacy `onLaunchMacro` finishes
   *    (preserves existing behavior — gameReady fires once at the end).
   *  - `StatefulMacroCoordinator` when the macro tree walk reaches a leaf node
   *    (`status: 'step', isLeaf: true`).
   *
   * Idempotent within a single launch. The second and subsequent calls are
   * silent no-ops; the gateway is reset on the next `gameLaunched` event so a
   * subsequent launch can emit `gameReady` again. This guards against:
   *  - The stateful coordinator + a late legacy fallback both signalling.
   *  - Multiple `MacroWalkResponse { isLeaf: true }` arriving (e.g. duplicated
   *    on WS reconnect) for the same launch.
   */
  public signalLaunchMacroWalkComplete(): void {
    if (this.launchMacroWalkComplete) {
      console.log('[GameLaunchCoordinator] signalLaunchMacroWalkComplete: already signalled this launch — no-op');
      return;
    }

    this.launchMacroWalkComplete = true;

    if (!this.lastLaunchData) {
      console.warn('[GameLaunchCoordinator] signalLaunchMacroWalkComplete called before any gameLaunched event — skipping gameReady emit');
      return;
    }

    console.log('[GameLaunchCoordinator] ✅ Launch macro walk complete, emitting gameReady');
    this.events.gameReady.emit(this.lastLaunchData);
  }

  /**
   * Run the legacy `onLaunchMacro` and emit `gameReady` afterwards.
   *
   * Code-move-only refactor of the prior inline behavior in the `gameLaunched`
   * listener. The pre-refactor sequence was:
   *
   *   gameLaunched -> await macroManager.executeOnLaunchMacro() -> emit gameReady
   *
   * This method preserves that exact sequence. It is invoked by:
   *  - The `gameLaunched` listener when `useStatefulMacroSystem !== true`
   *    (default path).
   *  - `StatefulMacroCoordinator` when the API responds with `status: 'disabled'`
   *    or `status: 'error'` and we need to fall back to the legacy macro.
   *
   * Calls `signalLaunchMacroWalkComplete()` at the end, which itself is
   * idempotent — so multiple invocations during a single launch (e.g. legacy
   * listener + stateful disabled fallback) collapse to a single gameReady emit.
   */
  public async executeLegacyOnLaunchMacro(): Promise<void> {
    const macroGeneration = this.launchMacroGeneration;
    if (this.managers.macro) {
      await this.managers.macro.executeOnLaunchMacro();
    } else {
      console.warn('[GameLaunchCoordinator] ⚠️  MacroManager not initialized, skipping legacy onLaunchMacro');
    }
    if (macroGeneration === this.launchMacroGeneration) this.signalLaunchMacroWalkComplete();
  }

  /**
   * Returns true iff the currently-loaded campaign config opts into the
   * stateful macro tree walk (Phase 1 schema flag).
   *
   * False if no campaign is loaded yet — defensive default; the legacy path
   * is the safe fallback when we can't tell.
   */
  private shouldUseStatefulMacroSystem(): boolean {
    const cfg = this.pavilionCoordinator?.getCampaignConfig();
    return cfg?.useStatefulMacroSystem === true;
  }

  /**
   * Handle replication message from Electron (via IPC)
   *
   * This is called by ReplicationRouter when Electron sends playjectorHostModeState updates.
   * Playjector runs in the Electron main process and replicates its state to the renderer.
   *
   * NOTE: This is the ONLY way GameLaunchCoordinator receives Playjector state in production!
   * MockPlayjectorService simulates this by calling onStateChange() with similar data.
   *
   * @param unknownState - playjectorHostModeState from Electron (needs type narrowing)
   */
  public onReplicationReceived(unknownState: unknown): void {
    console.log('[GameLaunchCoordinator] Received replication from Electron:', unknownState);

    // Type narrowing: cast to PlayjectorHostEdition
    const state = unknownState as PlayjectorHostEdition;

    // Extract game launch state
    const { gameLaunched, gameId, gameName, steamLaunched, steamWindow, userLoggedIn } = state;

    // Update game mode store - manager listens to changes and emits events
    // This is the same pattern as handlePlayjectorStateChange()
    this.gameModeStore.updateState({
      gameLaunched,
      gameId,
      gameName,
      steamLaunched,
      steamWindow,
      userLoggedIn,
    });

    // NOTE: Lifecycle manager automatically detects store changes and emits events.
    // No need to call manager methods directly.
  }

  // ========================================
  // Private - API Integration
  // ========================================

  // NOTE: GameLaunchCoordinator does NOT send API messages directly!
  // It emits events that PavilionCoordinator listens to.
  // PavilionCoordinator owns matchmaking state and handles API communication.

  // ========================================
  // Private - Cross-Coordinator Integration
  // ========================================

  /**
   * Notify Playjector of Pavilion state changes
   * This is the PROPER architecture: GameLaunchCoordinator listens to PavilionCoordinator events
   * and decides when to call the service
   */
  private notifyPlayjectorOfPavilionState(pavilionCoordinator: PavilionCoordinator): void {
    if (!this.playjectorService) {
      console.warn('[GameLaunchCoordinator] Service not initialized');
      return;
    }

    if (!pavilionCoordinator) {
      console.warn('[GameLaunchCoordinator] PavilionCoordinator not provided');
      return;
    }

    // Read state from PavilionCoordinator (via public API)
    const invitation = pavilionCoordinator.getInvitation();
    const credentials = pavilionCoordinator.getCredentials();
    const campaign = pavilionCoordinator.getCampaignDetails();
    const hostState = pavilionCoordinator.getHostMatchmakingState();
    const authId = pavilionCoordinator.getAuthId();

    // Determine Steam guest login setting from invitation
    const useSteamGuestLogin =
      invitation?.type === 'cafe' &&
      invitation.manifest?.useSteamGuestLogin === true;

    // Format credentials
    const credentialsToSend = credentials.username && credentials.password
      ? `${credentials.username} ${credentials.password}`
      : '';

    console.log('[GameLaunchCoordinator] Notifying Playjector of Pavilion state:', {
      edition: 'pavilion',
      matchmakeable: hostState.matchmakeable,
      useSteamGuestLogin,
      guestAccount: hostState.guestAccount,
      hasCredentials: !!credentialsToSend,
      authId,
      campaignId: campaign.campaignId,
    });

    // Call service (this coordinator owns the service!)
    this.playjectorService.sendHostModeChanged(
      'pavilion',
      hostState.matchmakeable,
      useSteamGuestLogin,
      hostState.guestAccount,
      credentialsToSend,
      authId,
      campaign.campaignId,
    );
  }

  /**
   * Launch a campaign with timeout tracking
   * This method starts the campaign switch flow and tracks the result
   */
  private async launchCampaignWithTracking(pavilionCoordinator: PavilionCoordinator): Promise<void> {
    const launchGeneration = ++this.campaignLaunchGeneration;
    const campaign = pavilionCoordinator.getCampaignDetails();
    const campaignId = campaign.campaignId;
    const campaignConfig = campaign.config;

    if (!campaignId) {
      console.warn('[GameLaunchCoordinator] Cannot launch campaign: no campaign ID');
      Sentry.logger.warn('[GameLaunchCoordinator] Cannot launch campaign: no campaign ID')
      return;
    }

    if (!campaignConfig) {
      console.error('[GameLaunchCoordinator] Cannot launch campaign: no campaign config');
      Sentry.logger.error('[GameLaunchCoordinator] Cannot launch campaign: no campaign config')
      return;
    }

    // Interim (B): host-wide game-save restore/reset is v1-only until per-instance
    // saves exist. V2 multi-instance campaigns skip prep so they don't stall in
    // game_save_restore while MatchLifecycleManager owns per-player sessions.
    const preparation = campaignConfig.matchmakingVersion === 'v2'
      ? { status: 'ready' as const, configured: false }
      : await this.prepareGameSaveBeforeCampaignLaunch(
        pavilionCoordinator, campaignId, campaignConfig, launchGeneration);
    if (preparation.status === 'failed') {
      this.reportCampaignSwitchFailure(campaignId, preparation.reason);
      return;
    }
    if (preparation.status === 'superseded') {
      this.failIfCampaignConfigChangedDuringRestore(pavilionCoordinator, campaignId, campaignConfig, launchGeneration);
      return;
    }
    const configured = preparation.configured;

    console.log('[GameLaunchCoordinator] Starting campaign launch with tracking:', campaignId);
    Sentry.logger.warn(`[GameLaunchCoordinator] Starting campaign launch with tracking: ${campaignId}`); // This is temp!
    
    this.analytics.sendGameLaunchAttemptedEvent({
      campaignId,
      gameName: campaignConfig.displayName,
    });

    // DEBUG: Log the full campaign config to see what we received from API
    console.log('[GameLaunchCoordinator] 📋 Full campaign config:', {
      campaignId: campaignConfig.campaignId,
      displayName: campaignConfig.displayName,
      edition: campaignConfig.edition,
      active: campaignConfig.active,
      launchPackage: campaignConfig.launchPackage,
    });

    // Get game-specific ready wait time (CampaignSwitchManager will add the base timeout)
    const readyWaitTime = campaignConfig.launchPackage?.readyWaitTimeMs || 0;
    const launchPackage = campaignConfig.launchPackage as ProcessLaunchPackage;

    // Android multi-instance Phase 3: single-launch path; all other launchers
    // keep the legacy flow below byte-for-byte.
    if (launchPackage.launcher === 'android') {
      this.notifyPlayjectorOfPavilionState(pavilionCoordinator);
      this.applyHostCapacity();
      await this.runAndroidCampaign(campaignId, pavilionCoordinator.getAuthId(), campaignConfig);
      return;
    }

    if (this.gameInstanceLaunchManager.getSessions().length > 0) {
      await this.cleanupGameInstances(pavilionCoordinator.getAuthId());
    }

    // Start tracking with timeout (pass only readyWaitTime, manager adds base timeout)
    const switchPromise = this.managers.campaignSwitch.startSwitch(campaignId, readyWaitTime);

    // Update Playjector with host mode/credentials (must come before launch)
    this.notifyPlayjectorOfPavilionState(pavilionCoordinator);

    // Get auth ID for launch command
    const authId = pavilionCoordinator.getAuthId();

    // CRITICAL: Actually launch the game via processLaunch IPC message
    console.log('[GameLaunchCoordinator] 🎮 Sending processLaunch IPC command:', {
      campaignId,
      displayName: campaignConfig.displayName,
      launcher: campaignConfig.launchPackage?.launcher,
      authId,
    });

    Sentry.logger.warn('[GameLaunchCoordinator] 🎮 Sending processLaunch IPC command:', { // This is temp!
      campaignId,
      displayName: campaignConfig.displayName,
      launcher: campaignConfig.launchPackage?.launcher,
    });

    // Scope subsequent gameLaunched/gameReady events to this restored launch.
    // The mock can deliver ready before the launch reply; Pavilion buffers it
    // until a SUCCESS reply for this generation. Older events stay gated.
    if (configured) this.events.gameSaveLaunchDispatched.emit({ campaignId, generation: launchGeneration });
    if (!this.isCurrentCampaignLaunch(pavilionCoordinator, campaignId, campaignConfig, launchGeneration)) {
      if (launchGeneration === this.campaignLaunchGeneration) {
        this.managers.campaignSwitch.cancelPendingSwitch('game_save_launch_superseded');
      }
      this.failIfCampaignConfigChangedDuringRestore(pavilionCoordinator, campaignId, campaignConfig, launchGeneration);
      return;
    }
    this.playjectorService.launchCampaign(
      authId,
      'pavilion',
      campaignConfig.launchPackage,
    );

    // Wait for result (this will resolve when result is received or timeout fires)
    const result = await switchPromise;
    // Ordinary disconnect refreshes can replace an unmanaged campaign's config
    // after game-ready but before this reply. Restored saves still pin that config.
    if (!this.isCurrentCampaignLaunch(pavilionCoordinator, campaignId, campaignConfig, launchGeneration, configured)) {
      this.failIfCampaignConfigChangedDuringRestore(pavilionCoordinator, campaignId, campaignConfig, launchGeneration);
      console.log('[GameLaunchCoordinator] Ignoring launch result for a superseded campaign');
      return;
    }

    if (result.success) {
      console.log('[GameLaunchCoordinator] ✅ Campaign launch successful:', campaignId);
      if (configured) this.events.gameSaveLaunchConfirmed.emit({ campaignId, generation: launchGeneration });
      this.reportCampaignSwitchSuccess(campaignId);
    } else {
      console.error('[GameLaunchCoordinator] ❌ Campaign launch failed:', result.reason);
      Sentry.logger.error(`[GameLaunchCoordinator] ❌ Campaign launch failed: ${result.reason}`);
      this.reportCampaignSwitchFailure(campaignId, result.reason, result.transient);
    }
  }

  /** Android Phase 3 branch entry; the launch flow lives in `android/AndroidCampaignLauncher`. */
  private runAndroidCampaign(
    campaignId: string,
    hostAuthId: string,
    campaignConfig: HostCampaignConfig,
  ): Promise<void> {
    const capabilities = this.instanceCapabilitiesResolver();
    // Raise default maxPlayers=1 to the campaign's requested instance count so
    // multi-emulator hosts are not clamped to a single HOST_STATE_REPORT slot.
    const configMaxPlayers = alignHostCapacityForCampaign(campaignConfig, capabilities);
    return this.androidLauncher.runCampaign(campaignId, hostAuthId, campaignConfig, {
      configMaxPlayers,
      hardwareCap: hardwareCapFromGamingScore(this.hardwareInfoProvider()) ?? undefined,
      capabilities,
    });
  }

  /** Select/check/restore phases finish before a campaign launch tracker is created. */
  private async prepareGameSaveBeforeCampaignLaunch(
    pavilion: PavilionCoordinator, campaignId: string, configuration: HostCampaignConfig, generation: number,
  ): Promise<CampaignSavePreparation> {
    const current = () => this.isCurrentCampaignLaunch(pavilion, campaignId, configuration, generation);
    try { this.campaignGameSaveConfiguration?.updateCampaignConfiguration(configuration); }
    catch { return { status: 'failed', reason: 'Dashboard game save settings are invalid' }; }
    this.events.gameSaveCampaignConfigurationSelected.emit({ campaignId, generation, configuration: configuration.gameSave });
    if (!current()) return { status: 'superseded' };
    this.gameSaveRestoreGeneration = generation;
    try {
      const campaign = this.gameSaveCampaignFromConfig(configuration);
      const configured = await this.hasConfiguredGameSave(campaign, configuration);
      if (!current()) return { status: 'superseded' };
      this.events.gameSaveConfigurationChecked.emit({ campaignId, configured });
      if (!current()) return { status: 'superseded' };
      if (configured) {
        if (!campaign) throw new Error('Configured game save has no supported game identity');
        await this.restoreGameSaveBeforeCampaignLaunch(pavilion, campaign, configuration, generation);
        if (!current()) return { status: 'superseded' };
      }
      return { status: 'ready', configured };
    } catch (error) {
      return current() ? { status: 'failed', reason: error instanceof Error ? error.message : String(error) }
        : { status: 'superseded' };
    } finally {
      if (this.gameSaveRestoreGeneration === generation) this.gameSaveRestoreGeneration = null;
    }
  }

  private async restoreGameSaveBeforeCampaignLaunch(
    pavilion: PavilionCoordinator, campaign: GameSaveCampaign, configuration: HostCampaignConfig, generation: number,
  ): Promise<void> {
    const current = () => this.isCurrentCampaignLaunch(pavilion, campaign.campaignId, configuration, generation);
    this.launchMacroGeneration += 1;
    this.launchMacroWalkComplete = true;
    this.lastLaunchData = null;
    this.events.gameSaveRestoreStarted.emit({ campaignId: campaign.campaignId });
    if (!current()) return;
    const requireStopped = configuration.gameSave?.enabled === true ? configuration.gameSave.requireStopped : true;
    // Steam's tracked process must release its save; native loadGame stops the selected Android VM.
    if (configuration.launchPackage.launcher === 'steam' && requireStopped) {
      await this.stopCurrentGameBeforeRestore(pavilion.getAuthId());
    }
    if (!current()) return;
    await this.restoreConfiguredGameSave(campaign, requireStopped);
    if (!current()) return;
    this.events.gameSaveRestored.emit({ campaignId: campaign.campaignId });
  }

  private async stopCurrentGameBeforeRestore(hostAuthId: string): Promise<void> {
    if (!this.playjectorService.getCurrentState().gameLaunched) return;

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let unsubscribe: () => void = () => undefined;
      const timeout = setTimeout(() => {
        finish(new Error('Timed out waiting for the running game to stop before restoring its save'));
      }, GAME_SAVE_PROCESS_STOP_TIMEOUT_MS);
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        unsubscribe();
        if (error) reject(error);
        else resolve();
      };

      try {
        unsubscribe = this.playjectorService.onStateChange((state) => {
          if (!state.gameLaunched) finish();
        });
        if (settled) {
          unsubscribe();
          return;
        }
        this.playjectorService.terminateGame(hostAuthId);
        if (!this.playjectorService.getCurrentState().gameLaunched) finish();
      } catch (error) {
        finish(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  private gameSaveCampaignFromConfig(campaignConfig: HostCampaignConfig): GameSaveCampaign | null {
    const launchPackage = campaignConfig.launchPackage;
    if (launchPackage.launcher === 'steam') {
      return {
        campaignId: campaignConfig.campaignId,
        launchPackage: { launcher: 'steam', launchId: launchPackage.launchId },
      };
    }
    if (launchPackage.launcher === 'android') {
      return {
        campaignId: campaignConfig.campaignId,
        launchPackage: { launcher: 'android', package: launchPackage.package },
      };
    }
    return null;
  }

  private async hasConfiguredGameSave(campaign: GameSaveCampaign | null, configuration: HostCampaignConfig): Promise<boolean> {
    if (!campaign) return false;
    const key = JSON.stringify(campaign);
    if (configuration.gameSave?.enabled === false) {
      this.confirmedConfiguredSaveKeys.delete(key);
      return false;
    }
    let lookup: GameSaveConfigurationAvailabilityResult;
    try {
      lookup = (await this.gameSaveConfigurationAvailability?.hasGameSaveConfiguration(campaign)) ?? {
        status: 'unavailable' as const,
        error: { code: 'internal' as const, retryable: true, message: 'Native game save configuration service is unavailable' },
      };
    } catch (error) {
      lookup = {
        status: 'unavailable' as const,
        error: { code: 'internal' as const, retryable: true, message: error instanceof Error ? error.message : String(error) },
      };
    }

    if (lookup.status === 'configured') {
      this.confirmedConfiguredSaveKeys.add(key);
      return true;
    }
    if (configuration.gameSave?.enabled === true) {
      const detail = lookup.status === 'unavailable'
        ? `${lookup.error.code}: ${lookup.error.message}` : 'Native process reported no game save configuration';
      throw new Error(`Dashboard-configured game save cannot be verified; refusing to launch without reset (${detail})`);
    }
    if (this.confirmedConfiguredSaveKeys.has(key)) {
      throw new Error('Previously configured game save cannot be verified; refusing to launch without reset');
    }
    if (lookup.status === 'unavailable') {
      // Preserve legacy launches for games that have never been confirmed to
      // use managed saves. A known configured game remains fail-closed above.
      console.warn('[GameLaunchCoordinator] Save configuration unavailable; launching unconfirmed game normally:', lookup.error.message);
    }
    return false;
  }

  private async restoreConfiguredGameSave(campaign: GameSaveCampaign, requireStopped: boolean): Promise<void> {
    if (!this.gameSaveManager) throw new Error('Game save manager is unavailable');
    const result = await this.gameSaveManager.loadGame({ campaign, requireStopped });
    if (result.status === 'failed') throw new Error(`Game save restore failed: ${result.error.message}`);
  }

  private isCurrentCampaignLaunch(
    pavilionCoordinator: PavilionCoordinator,
    campaignId: string,
    campaignConfig: HostCampaignConfig,
    launchGeneration: number,
    requirePinnedConfiguration = true,
  ): boolean {
    const current = pavilionCoordinator.getCampaignDetails();
    return launchGeneration === this.campaignLaunchGeneration &&
      current.campaignId === campaignId &&
      (!requirePinnedConfiguration || current.config === campaignConfig);
  }

  private failIfCampaignConfigChangedDuringRestore(
    pavilionCoordinator: PavilionCoordinator,
    campaignId: string,
    campaignConfig: HostCampaignConfig,
    launchGeneration: number,
  ): void {
    const current = pavilionCoordinator.getCampaignDetails();
    if (launchGeneration === this.campaignLaunchGeneration &&
      current.campaignId === campaignId && current.config !== campaignConfig) {
      this.reportCampaignSwitchFailure(campaignId, 'Campaign config changed during game save restore');
    }
  }

  /**
   * Drive v1 machine-level game state for the android path through the same
   * store Playjector replication writes on the legacy path (v1 coherence).
   * `gameId` is 0 — android campaigns carry no numeric id.
   */
  private driveAndroidV1GameState(launched: boolean, gameName: string): void {
    const current = this.gameModeStore.currentState;
    if (current.gameLaunched === launched) return;
    this.gameModeStore.updateState({
      ...current,
      gameLaunched: launched,
      gameId: 0,
      gameName: launched ? gameName : current.gameName,
    });
  }

  private handleCampaignLaunchResult(result: CampaignLaunchResult): void {
    console.log('[GameLaunchCoordinator] Received campaign launch result:', result);

    const { result: resultType, message } = result;

    if (result.sessionId) {
      const normalizedResult = resultType === 'SUCCESS' ? 'SUCCESS' as const : 'FAILURE' as const;
      const handled = this.gameInstanceLaunchManager.handleLaunchReply(
        result.sessionId,
        normalizedResult,
        message,
      );
      if (handled) {
        this.events.instanceSessionsChanged.emit({
          sessions: this.gameInstanceLaunchManager.getSessions(),
        });
        return;
      }
    }

    // Aggregate FAILURE with no sessionId while an android multi-instance launch
    // is in flight: the android path never registers a campaignSwitch pending
    // switch, so routing this to CampaignSwitchManager only logs "no pending
    // switch" and every per-instance waiter waits out its 120s reply timeout.
    // Instead, fail that stream's waiters immediately with the real reason.
    if (!result.sessionId && resultType !== 'SUCCESS') {
      const activeStreamId = this.gameInstanceLaunchManager.getActiveAndroidStreamId();
      if (activeStreamId) {
        const reason = message || 'Android multi-instance launch failed';
        this.gameInstanceLaunchManager.failActiveAndroidStreamLaunches(activeStreamId, reason);
        this.events.instanceSessionsChanged.emit({
          sessions: this.gameInstanceLaunchManager.getSessions(),
        });
        console.log(
          '[GameLaunchCoordinator] Failed active android stream launches from aggregate reply:',
          activeStreamId,
          reason,
        );
        return;
      }
    }

    this.managers.campaignSwitch.reportResult(resultType, message);
    console.log('[GameLaunchCoordinator] Reported result to CampaignSwitchManager');
  }

  /**
   * Report successful campaign switch
   *
   * Emits event for PavilionCoordinator to update matchmaking state and notify API
   */
  private reportCampaignSwitchSuccess(campaignId: string): void {
    console.log('[GameLaunchCoordinator] ✅ Campaign switch succeeded:', campaignId);

    // Emit event - PavilionCoordinator will listen and update its own state + notify API
    this.events.campaignSwitchSucceeded.emit({ campaignId });
  }

  /**
   * Report failed campaign switch.
   *
   * Public so the StatefulMacroCoordinator can escalate `macro_walk_max_retries`
   * through the same downstream pipeline as a regular launch failure
   * (PavilionCoordinator listens for `campaignSwitchFailed` and emits
   * `MATCHMAKER_CAMPAIGN_SWITCH_FAILURE` to the API). Internal callers continue
   * to use this for the existing tracker-result-based failure path.
   *
   * Emits event for PavilionCoordinator to update matchmaking state and notify API.
   */
  public reportCampaignSwitchFailure(campaignId: string, reason: string, transient?: boolean): void {
    console.error('[GameLaunchCoordinator] ❌ Campaign switch failed:', campaignId, reason);

    if (this.managers.campaignSwitch.getPendingCampaignId() === campaignId) {
      // External deadlines must settle the tracker before downstream cleanup.
      // Invalidate its continuation so cancellation cannot produce a second failure or late success.
      this.campaignLaunchGeneration += 1;
      this.managers.campaignSwitch.cancelPendingSwitch(reason);
    }

    // Emit event - PavilionCoordinator will listen and update its own state + notify API
    this.events.campaignSwitchFailed.emit({ campaignId, reason, transient: transient ?? false });
  }

  /**
   * Cleanup coordinator (for testing/unmounting)
   *
   * CRITICAL: Cleans up all timers and pending operations
   */
  public destroy(): void {
    console.log('[GameLaunchCoordinator] Destroying coordinator...');

    // Cancel any pending campaign switches (clears timeout)
    this.managers.campaignSwitch.cancelPendingSwitch('coordinator destroyed');

    // Unsubscribe from service
    if (this.unsubscribeFromService) {
      this.unsubscribeFromService();
      this.unsubscribeFromService = undefined;
    }

    if (this.unsubscribeInstanceLaunched) {
      this.unsubscribeInstanceLaunched();
      this.unsubscribeInstanceLaunched = undefined;
    }

    this.gameInstanceReportManager.stopReporting();
    this.matchLifecycleManager.detach();

    if (this.capacityConfigListenerId) {
      hostCapacityConfigState.removeListener(this.capacityConfigListenerId);
      this.capacityConfigListenerId = null;
    }

    console.log('[GameLaunchCoordinator] Destroyed');
  }
}

// ========================================
// NOTE: No singleton export here!
// ========================================
//
// GameLaunchCoordinator instances are created and managed by init.ts
// This avoids loading GlobalConfig at module load time (which breaks tests).
//
// Import from init.ts instead:
// import { playjectorCoordinator } from './coordinators/init';
